import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useBeginSaiSurveyProspectUploadMutation,
  useReplaceSaiSurveyProspectsMutation,
} from '../../../../../graphQL';
import { postUploadData } from '../../../../../utils';
import { GENERIC_UPLOAD_ERROR_MESSAGE } from '../constants';
import { ProspectUploadState } from '../types';
import { describeRejection, unwrapProspectResult } from '../utils';

type Args = {
  surveyId: number;
  onCommitted: (prospectCount: number) => void;
};

/**
 * Puts a prospect CSV in S3, then waits. Nothing replaces the survey's existing
 * list until `commit` runs, which is what the modal's "Update List" button calls.
 *
 * The CSV is not read until that commit, so row-level problems surface from it
 * rather than on drop. A commit that fails leaves the survey's current roster
 * exactly as it was — the ingest publishes a generation only once every row is
 * in, and readers never see an unpublished one.
 */
export const useProspectUploadLogic = ({ surveyId, onCommitted }: Args) => {
  const [beginUpload] = useBeginSaiSurveyProspectUploadMutation();
  const [replaceProspects] = useReplaceSaiSurveyProspectsMutation();

  const [state, setState] = useState<ProspectUploadState>({ status: 'idle' });

  /**
   * Nothing here is abortable — the S3 POST and the mutations run to completion
   * whatever the admin does. So each run claims a generation, and a run whose
   * generation has been superseded stops writing state. Without this, cancelling
   * mid-upload looks like it worked and is then silently undone: the pending
   * chain finishes and re-stages the file, arming "Update List" with the roster
   * the admin just abandoned.
   */
  const generationRef = useRef(0);
  const startRun = () => {
    generationRef.current += 1;
    const generation = generationRef.current;
    return (next: ProspectUploadState) => {
      if (generationRef.current === generation) setState(next);
    };
  };

  /**
   * Unmounting is a superseding event like any other: the S3 POST is not
   * abortable, so closing the modal mid-upload leaves a request that will land
   * and try to stage a file nobody is waiting for. Bumping the generation on
   * teardown makes that write a no-op instead of a setState on a dead hook.
   */
  useEffect(
    () => () => {
      generationRef.current += 1;
    },
    []
  );

  const reset = useCallback(() => {
    generationRef.current += 1;
    setState({ status: 'idle' });
  }, []);

  const onDropRejected = useCallback((files: File[]) => {
    generationRef.current += 1;
    setState({ status: 'error', message: describeRejection(files) });
  }, []);

  const onDropAccepted = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;
      const fileName = file.name;
      const advance = startRun();

      const fail = (message: string, detail?: string) =>
        advance({ status: 'error', fileName, message, detail });

      advance({ status: 'uploading', fileName, progress: 25 });
      try {
        const beginResponse = await beginUpload({ variables: { surveyId } });
        const signedUpload = beginResponse.data?.beginSaiSurveyProspectUpload.signedUpload;
        if (!signedUpload) return fail(GENERIC_UPLOAD_ERROR_MESSAGE);

        advance({ status: 'uploading', fileName, progress: 66 });

        const s3Response = await postUploadData(file, signedUpload.url, signedUpload.fields);
        if (!s3Response.ok) return fail(GENERIC_UPLOAD_ERROR_MESSAGE);

        advance({ status: 'staged', fileName, uploadId: signedUpload.uploadId });
      } catch (e) {
        fail(GENERIC_UPLOAD_ERROR_MESSAGE);
        // rethrow so sentry sees the underlying network/S3 failure
        throw e;
      }
    },
    [beginUpload, surveyId]
  );

  const commit = useCallback(async () => {
    if (state.status !== 'staged') return;
    const { fileName, uploadId } = state;
    const advance = startRun();

    advance({ status: 'committing', fileName });
    try {
      const replaceResponse = await replaceProspects({
        variables: { data: { surveyId, uploadId } },
      });
      const replaceResult = replaceResponse.data?.replaceSaiSurveyProspects;
      if (!replaceResult) {
        advance({ status: 'error', fileName, message: GENERIC_UPLOAD_ERROR_MESSAGE });
        return;
      }

      const unwrapped = unwrapProspectResult(replaceResult);
      if (!unwrapped.ok) {
        advance({
          status: 'error',
          fileName,
          message: unwrapped.message,
          detail: unwrapped.detail,
        });
        return;
      }

      onCommitted(unwrapped.prospectCount);
    } catch (e) {
      advance({ status: 'error', fileName, message: GENERIC_UPLOAD_ERROR_MESSAGE });
      // rethrow so sentry sees the underlying failure
      throw e;
    }
  }, [state, replaceProspects, surveyId, onCommitted]);

  return { state, onDropAccepted, onDropRejected, commit, reset };
};
